import { z } from "zod";

// Namespaced tag string: "<ns>:<value>". Both halves slug-format
// (lowercase alphanumeric + dash). 80-char ceiling matches the rest of
// the schema family. The lower bound (3) enforces at least one char per
// half plus the colon.
//
// Why namespacing instead of a flat string set:
//   - filters can be precise ("show me all `tag:warmup` at `location:home`")
//     without ambiguity from collisions.
//   - migrations stamp `program:` / `session:` deterministically without
//     invading user-authored tags.
//   - per-namespace aggregates are trivial.
//
// Free-text notes do NOT live in tags — LogSet.note carries them.
const TAG_RE = /^[a-z][a-z0-9-]*:[a-z0-9][a-z0-9-]*$/;

export const Tag = z
  .string()
  .min(3)
  .max(80)
  .regex(TAG_RE, "tag must be <namespace>:<slug>");
export type Tag = z.infer<typeof Tag>;

// Reserved namespaces. System-stamped ones are written by the API from
// authoritative fields (sessionId, fromPlanId); the user can write
// them in a payload but the server overwrites them on the way through.
// User-stamped ones the server passes through verbatim.
export const SYSTEM_TAG_NAMESPACES = [
  "program",
  "session",
  "fromplan",
] as const;
export type SystemTagNamespace = (typeof SYSTEM_TAG_NAMESPACES)[number];

export const USER_TAG_NAMESPACES = [
  "location",
  "equipment-instance",
  "tag",
  "flag",
  "superset",
] as const;
export type UserTagNamespace = (typeof USER_TAG_NAMESPACES)[number];

// EQ-1 — the two namespaces the equipment model writes, named once so the
// schema that validates the bare value and the seam that prefixes it can't
// drift apart.
export const EQUIPMENT_INSTANCE_NS = "equipment-instance";
export const LOCATION_NS = "location";

// Ceiling for a bare slug that a write seam will later prefix with a
// namespace. DERIVED, not chosen: Tag caps at 80 chars and the longer of
// the two prefixes above costs 19. A value above this passes its own
// field's slug check and is only rejected later, by Tag, once the prefix
// is applied — a failure surfacing at the write, far from the field that
// let it through.
export const NAMESPACED_VALUE_MAX = 80 - `${EQUIPMENT_INSTANCE_NS}:`.length;

// Read the value a namespaced tag carries, or `undefined` when the list has
// none. The one definition of how `<ns>:<value>` is parsed off a tag list.
//
// ⭐ Exists because the operation was about to be written a second time. The
// assembler recovers `equipment-instance:`/`location:` off a set's tags to
// rebuild an entry (workoutAssembly.ts); the SESSION_RESULT compute needs the
// SAME read to group its aggregate rows per machine (EQ-1-HIST) — and a second
// hand-rolled `tags.find(t => t.startsWith(...))` is a second place where the
// two could disagree about, say, an empty value or a colon inside the value.
//
// An empty value reads as absent: `equipment-instance:` with nothing after the
// colon is a malformed stamp, not a machine named "".
export const readTagValue = (
  tags: readonly string[] | undefined,
  ns: string,
): string | undefined => {
  const prefix = `${ns}:`;
  const hit = tags?.find((t) => t.startsWith(prefix));
  const value = hit?.slice(prefix.length);
  return value ? value : undefined;
};

export const isSystemTagNamespace = (ns: string): ns is SystemTagNamespace =>
  (SYSTEM_TAG_NAMESPACES as readonly string[]).includes(ns);

export const isUserTagNamespace = (ns: string): ns is UserTagNamespace =>
  (USER_TAG_NAMESPACES as readonly string[]).includes(ns);

export const splitTag = (
  t: string,
): { namespace: string; value: string } | null => {
  const idx = t.indexOf(":");
  if (idx <= 0 || idx === t.length - 1) return null;
  return { namespace: t.slice(0, idx), value: t.slice(idx + 1) };
};

export const makeTag = (namespace: string, value: string): string =>
  `${namespace}:${value}`;

/**
 * Fold accents onto their base letters: "Genève" → "Geneve".
 *
 * Separate from slugifyTagValue, not folded into it, and deliberately so
 * (2026-08-13): that function is also the importer's and the catalog editor's,
 * and quietly changing what a given input slugifies to would change values
 * those paths have already written. This is offered to NEW free-text inputs —
 * EQ-1's place naming is the first — where nothing exists to disagree with.
 *
 * ⚠️ Without it, slugifyTagValue's `[^a-z0-9-\s]` DELETES the accented letter
 * rather than replacing it, so "Genève" becomes `genve`. Registered as
 * CN-slugify-diacritics in next/consolidation.md: whether the older callers
 * should fold too is a decision with a data cost, not a cleanup.
 */
export const foldDiacritics = (raw: string): string =>
  // U+0300..U+036F is the combining-diacritics block that NFD splits off.
  // Escaped, not literal: combining marks in source are invisible in a diff
  // and survive a careless copy-paste as something else.
  raw.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// Sanitise a free-form user input into the value half of a tag. Drops
// chars outside [a-z0-9-], collapses whitespace to single dash, lowercases.
// Used by the catalog editor and importer when seeding user tags from
// arbitrary text.
export const slugifyTagValue = (raw: string): string =>
  raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-\s]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
